import jApi, {
    ImapInboxMail,
    WIDGET_LEVEL_1,
    WIDGET_LEVEL_2,
    WIDGET_LEVEL_3,
    WidgetDisplayMode,
    WidgetEmailsImapType,
    WidgetWrapperLevel,
} from 'jamespot-user-api';
import { Toast, Widget } from 'jamespot-front-business';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { WidgetEmailsImapHeader } from './WidgetEmailsImap/WidgetEmailsImapHeader';
import { officeConfigurationInitialState, OfficeConfigurationPrompt } from './components/OfficeConfigurationPrompt';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { WidgetScroll } from './components/WidgetScroll';
import { WidgetStylePadding } from './components/WidgetStylePadding';
import { useAppSelector, useAppDispatch } from '../../../redux/store';

const WidgetEmailsImap = commonComponentsRegistry.getLazyComponent('WidgetEmailsImap');

const REFRESH_INTERVAL = 60 * 1000;

export type JRCWidgetEmailsImapProps = {
    widget: WidgetEmailsImapType;
    inplace: boolean;
    mode?: WidgetDisplayMode;
    levelFallback?: (level: WidgetWrapperLevel, url: string) => ReactNode | undefined;
};

export const JRCWidgetEmailsImapWrapper = ({ widget, mode, levelFallback }: JRCWidgetEmailsImapProps) => {
    const { uniqid } = widget;
    const dispatch = useAppDispatch();
    const [isConfigurationValid, setIsConfigurationValid] = useState<boolean | undefined>(undefined);
    const [doInterval, setDoInterval] = useState<boolean>(false);
    const [list, setList] = useState<ImapInboxMail[]>([]);
    const [url, setUrl] = useState<string | undefined>(undefined);
    const level = useAppSelector((state) => Widget.selectors.selectWidgetLevel(state, widget.uniqid), shallowEqual);

    const handleLogout = useCallback(
        (err?: any, reload?: boolean) => {
            if (err && err.error === 400) {
                jApi.office.setConfiguration(officeConfigurationInitialState);
            }
            if (reload) {
                window.location.reload();
                return;
            }

            setIsConfigurationValid(false);
            dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true, loading: false }));
        },
        [dispatch, uniqid],
    );

    const fetchUnreadEmailsCount = useCallback(() => {
        jApi.office
            .imapInboxGetUnreadEmailsCount()
            .then(({ result }) => {
                dispatch(Widget.slice.actions.setWidgetChipCount({ uniqid, count: result }));
                dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true }));
            })
            .catch(handleLogout);
    }, [dispatch, uniqid, handleLogout]);

    const handleLoading = useCallback(
        (loading: boolean) => dispatch(Widget.slice.actions.setWidgetState({ uniqid, loading })),
        [uniqid, dispatch],
    );

    const fetchProviderUrls = useCallback(() => {
        jApi.office
            .getProviderBaseUrls()
            .then(({ result }) => {
                setUrl(result.imap_inbox_url);
            })
            .finally(() => handleLoading(false));
    }, [handleLoading]);

    const handleLogin = useCallback(
        (isValid: boolean) => {
            setIsConfigurationValid(isValid);
            dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true }));
        },
        [dispatch, uniqid],
    );

    const fetchData = useCallback(() => {
        jApi.office
            .imapInboxGetEmails({ limit: 10 })
            .then(({ result }) => {
                setList(result);
                setDoInterval(true);
            })
            .catch(() => {
                dispatch(Toast.actions.error({ label: 'PLUGIN_Manager_Connection_Error' }));
                setIsConfigurationValid(false);
                dispatch(Widget.slice.actions.setWidgetState({ uniqid, initialized: true, loading: false }));
            })
            .finally(() => handleLoading(false));
    }, [handleLoading, dispatch, uniqid]);

    useEffect(() => {
        if (!doInterval) return;
        if (!isConfigurationValid) return;
        if (level === WIDGET_LEVEL_1) return;
        const interval = setInterval(() => {
            if (level === WIDGET_LEVEL_2) {
                fetchUnreadEmailsCount();
            }
            if (level === WIDGET_LEVEL_3) {
                fetchUnreadEmailsCount();
                fetchData();
            }
        }, REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [isConfigurationValid, fetchData, doInterval, fetchUnreadEmailsCount, level]);

    useEffect(() => {
        jApi.office
            .isConfigurationValid()
            .then(({ result }) => handleLogin(result))
            .catch(handleLogout);
    }, [handleLogin, handleLogout]);

    useEffect(() => {
        if (!isConfigurationValid) return;
        if (level === WIDGET_LEVEL_1) {
            handleLoading(true);
            fetchProviderUrls();
            return;
        }
        if (level === WIDGET_LEVEL_2) {
            handleLoading(true);
            fetchUnreadEmailsCount();
            fetchProviderUrls();
            return;
        }

        handleLoading(true);
        fetchUnreadEmailsCount();
        fetchData();
    }, [isConfigurationValid, level, fetchProviderUrls, fetchData, handleLoading, fetchUnreadEmailsCount, dispatch]);

    if (isConfigurationValid && level && level !== WIDGET_LEVEL_3 && levelFallback) {
        return <>{levelFallback(level, url ?? '')}</>;
    }

    return (
        <WidgetScroll
            header={() => {
                return (
                    <>
                        <WidgetEmailsImapHeader
                            uniqid={uniqid}
                            isConfigurationValid={isConfigurationValid !== undefined && isConfigurationValid === true}
                            handleLogout={() => handleLogout(undefined, true)}
                        />
                    </>
                );
            }}
            scrollable={() => {
                return (
                    <WidgetStylePadding uniqid={uniqid}>
                        {isConfigurationValid === undefined && <></>}
                        {isConfigurationValid !== undefined && isConfigurationValid === false && mode === 'view' && (
                            <OfficeConfigurationPrompt />
                        )}
                        {isConfigurationValid && <WidgetEmailsImap emailList={list} />}
                    </WidgetStylePadding>
                );
            }}
        />
    );
};
